// -----------------------------------------------------------------------------
// PayoutClaimPage.jsx  — creator-facing invite‑claim screen
// • Reads :token from URL
// • Fetches payout via token (public endpoint)
// • Lets creator:
//
//   1. enter / confirm PayPal e‑mail
//   2. pick “Get Paid Now (‑5 %)”  or  “Wait 30 days – full amt”
//   3. submit → POST /api/payouts/claim/confirm/:token
//   4. optional: if they already have a Blitz login, nudge to /creatorconnect/login
//
// -----------------------------------------------------------------------------
// UI libs
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  CircularProgress,
  Divider,
  Paper,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  Toolbar,
  ThemeProvider,
} from "@mui/material";

// project imports
import HomeHeader from "../../Components/HomeHeader";
import HomeFooter from "../../Components/HomeFooter";
import { lightTheme } from "../../Utils/baseTheme";
import { globalStyles } from "../../Utils/Styles";
import client from "../../API";

// helpers
const money = (n = 0) =>
  `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const PayoutClaimPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [payout, setPayout] = useState(null);
  const [paypalEmail, setPaypalEmail] = useState("");
  const [speedChoice, setSpeedChoice] = useState("wait"); // wait | fast
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

  /* ───────── Fetch payout on mount ───────── */
  useEffect(() => {
    const fetchPayout = async () => {
      try {
        // backend exposes /api/payouts/token/:token  (read‑only)
        const { payout } = await client.payouts.byToken(token);
        setPayout(payout);
        setPaypalEmail(payout?.payout_email || "");
      } catch (err) {
        console.error(err);
        setSnack({
          open: true,
          msg: "Unable to load payout – please check the link.",
          sev: "error",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchPayout();
  }, [token]);

  if (loading) {
    return (
      <Box
        sx={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!payout) {
    return (
      <Box
        sx={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography>Payout not found.</Typography>
      </Box>
    );
  }

  /* ───────── submit handler ───────── */
  const handleSubmit = async () => {
    if (!paypalEmail) {
      setSnack({ open: true, msg: "Please enter your PayPal e‑mail", sev: "warning" });
      return;
    }
    try {
      await client.payouts.claim(token, {
        paymentMethod: paypalEmail,
        blitzpay: speedChoice === "fast",
      });
      setSnack({
        open: true,
        msg: "Payment method saved – your payout is on its way!",
        sev: "success",
      });
      setTimeout(() => navigate("/creatorconnect/login"), 1800);
    } catch (err) {
      console.error(err);
      setSnack({
        open: true,
        msg: err?.response?.data?.error || "Something went wrong.",
        sev: "error",
      });
    }
  };

  /* ───────── UI ───────── */
  return (
    <ThemeProvider theme={lightTheme}>
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          backgroundImage: "linear-gradient(to bottom, #ffffff, #e0e0e0)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <HomeHeader />
        <Toolbar />
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
          <Container
            sx={{
              ...globalStyles.wholeHeightMinusToolbar,
              maxWidth: "sm",
              py: 4,
            }}
          >
            <Paper elevation={3} sx={{ p: 4 }}>
              <Typography variant="h4" align="center" fontWeight="bold">
                Claim Your Payment
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography align="center" variant="h6">
                {payout.creator_id}, you have <b>{money(payout.amount)}</b>{" "}
                waiting for you.
              </Typography>

              <Divider sx={{ my: 4 }} />

              {/* PayPal input */}
              <TextField
                fullWidth
                required
                type="email"
                label="PayPal E‑mail"
                placeholder="you@example.com"
                value={paypalEmail}
                onChange={(e) => setPaypalEmail(e.target.value)}
              />

              {/* Speed choice */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  How fast do you want your money?
                </Typography>
                <RadioGroup
                  value={speedChoice}
                  onChange={(e) => setSpeedChoice(e.target.value)}
                >
                  <FormControlLabel
                    value="fast"
                    control={<Radio />}
                    label={`Get Paid Today (5 % fee) — you receive ${money(
                      payout.amount * 0.95
                    )}`}
                  />
                  <FormControlLabel
                    value="wait"
                    control={<Radio />}
                    label={`Wait 30 days (0 % fee) — you receive ${money(
                      payout.amount
                    )}`}
                  />
                </RadioGroup>
              </Box>

              <Button
                variant="contained"
                size="large"
                fullWidth
                sx={{ mt: 4 }}
                onClick={handleSubmit}
              >
                Confirm &amp; Continue
              </Button>

              {/* Already have Blitz */}
              {payout.has_creator_user && (
                <Button
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={() => navigate("/creatorconnect/login")}
                >
                  I already have a Blitz account
                </Button>
              )}
            </Paper>
          </Container>
        </Box>
        <HomeFooter />

        {/* Snackbar */}
        <Snackbar
          open={snack.open}
          autoHideDuration={5000}
          onClose={() => setSnack({ ...snack, open: false })}
        >
          <Alert
            onClose={() => setSnack({ ...snack, open: false })}
            severity={snack.sev}
            sx={{ width: "100%" }}
          >
            {snack.msg}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default PayoutClaimPage;
